// Minimal context for the AddAi Chat sidepanel — exposes open/close so
// header buttons (or anything else) can toggle the panel without prop
// drilling. The panel itself is rendered inside the dashboard layout
// (Dashboard.jsx) so that opening it pushes the main content over instead
// of overlaying it. The open/closed state is persisted to localStorage so
// it survives reloads.
//
// Ported from BOHSA CRM's chat integration, scoped to the PTS workspace.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

const ChatPanelContext = createContext(null);
const STORAGE_KEY = 'addai_chat_open';

export const useChatPanel = () => {
  const ctx = useContext(ChatPanelContext);
  if (!ctx) {
    // Returning a noop instead of throwing keeps the Header useful even
    // on routes the provider doesn't wrap (e.g. /login). The button
    // wouldn't open anything there anyway.
    return {
      isOpen: false, open: () => {}, close: () => {}, toggle: () => {},
      pageContext: null, setPageContext: () => {},
      customerLock: null, setCustomerLock: () => {},
    };
  }
  return ctx;
};

// usePageContext — one-line opt-in for any dashboard page to tell the chat
// panel what the operator is looking at, mirroring AiTables'
// ChatIframeSidebar embedContext pattern (see addai-tables/app/src/
// components/Chat/ChatIframeSidebar.jsx). Sets on mount / whenever `ctx`
// changes, clears on unmount so navigating away drops stale context rather
// than leaving the chat scoped to a page the operator has left.
//
// `ctx` shape (v2 embed-context envelope, matches embed.js's
// AddAiChat.buildContext):
//   { app: 'pts-crm', app_label: 'PTS CRM', items: [{ key, label, value, icon }], deep_link }
//
// Pass `null` (or omit) to explicitly clear — useful on list/search pages
// that don't represent a single customer/trace/pension.
export const usePageContext = (ctx) => {
  const { setPageContext } = useChatPanel();
  // Stringify for the dep array — callers usually pass a fresh object
  // literal each render, and we only want to re-push when the *content*
  // changes, not on every parent re-render.
  const serialized = ctx ? JSON.stringify(ctx) : null;
  useEffect(() => {
    setPageContext(ctx || null);
    return () => setPageContext(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);
};

// useCustomerLock — one-line opt-in for a customer-facing page (User /
// Trace / Pension / Search) to declare which PTS customer the operator is
// currently looking at. This does NOT open or create anything by itself —
// it just records intent. ChatPanel.jsx watches this alongside the panel's
// open/closed state and only fires the addai:open-customer-instance
// postMessage (which lazily finds-or-creates that customer's Tracey
// instance) when the panel is actually open, per the owner's requirement
// that instances are only created for customers an operator opens the
// chat on — never on page view or navigation alone.
//
// `lock` shape: { customerId, customerName, metadata } | null — see
// src/utilities/chatContext.js's buildCustomerLock.
export const useCustomerLock = (lock) => {
  const { setCustomerLock } = useChatPanel();
  const serialized = lock ? JSON.stringify(lock) : null;
  useEffect(() => {
    setCustomerLock(lock || null);
    return () => setCustomerLock(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);
};

export const ChatPanelProvider = ({ children }) => {
  // Restore the last open/closed state so the panel stays put across reloads.
  const [isOpen, setIsOpen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, isOpen ? '1' : '0');
    } catch {
      /* ignore storage failures (private mode / disabled storage) */
    }
  }, [isOpen]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  // Live page context — what the operator is currently looking at (customer /
  // trace / pension / search). Pushed into the mounted chat SDK instance via
  // ChatPanel's `chat.setContext(...)`, exactly like AiTables' postContext().
  const [pageContext, setPageContext] = useState(null);

  // Customer-lock intent — set by a customer-facing page (User / Trace /
  // Pension / Search) via useCustomerLock() when the operator is viewing a
  // specific customer. { customerId, customerName, metadata } | null.
  //
  // ChatPanel.jsx watches this alongside `isOpen` and fires the
  // addai:open-customer-instance postMessage (find-or-create that
  // customer's Tracey instance, entity_studio.entity_instance_find_or_create
  // RPC, live in prod) only while the panel is actually open — never on
  // page view/navigation alone. See task-pts-context-design.md §3.3 for the
  // wire contract and Phase 4 for how this got wired up.
  const [customerLock, setCustomerLock] = useState(null);

  const value = useMemo(
    () => ({ isOpen, open, close, toggle, pageContext, setPageContext, customerLock, setCustomerLock }),
    [isOpen, open, close, toggle, pageContext, customerLock]
  );

  return (
    <ChatPanelContext.Provider value={value}>
      {children}
    </ChatPanelContext.Provider>
  );
};

export default ChatPanelProvider;
